import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill"; // Import the Quill editor
import "react-quill/dist/quill.snow.css"; // Import the Quill styles
import { HiSparkles } from "react-icons/hi2";
import {
  stripPostPreviewHtml,
  wrapPostPreviewHtml,
} from "../../helpers/postBodyHtml";

const PostEditor = ({
  updateBodyContent,
  value,
  readOnly = false,
  editorSyncCount = 0,
  syncFromStoredBody = "",
  showAiFab = false,
  onAiFabClick,
  aiFabDisabled = false,
}) => {
  const [editorContent, setEditorContent] = useState(() =>
    stripPostPreviewHtml(value || "")
  );
  const syncBodyRef = useRef(syncFromStoredBody);
  syncBodyRef.current = syncFromStoredBody;

  useEffect(() => {
    if (value) {
      setEditorContent(stripPostPreviewHtml(value));
    }
  }, [value]);

  useEffect(() => {
    if (!editorSyncCount) return;
    setEditorContent(stripPostPreviewHtml(syncBodyRef.current || ""));
  }, [editorSyncCount]);

  const handleChange = (value) => {
    setEditorContent(value);
    updateBodyContent(wrapPostPreviewHtml(value));
  };

  return (
    // style is in App.css
    <div className="post-editor relative">
      <ReactQuill
        value={editorContent}
        onChange={handleChange}
        readOnly={readOnly}
        modules={
          readOnly
            ? { toolbar: false }
            : {
                toolbar: [
                  [{ header: "1" }, { header: "2" }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["bold", "italic", "underline"],
                  [{ align: [] }],
                  ["link"],
                  ["blockquote"],
                  ["clean"],
                ],
              }
        }
        formats={[
          "header",
          "font",
          "list",
          "bold",
          "italic",
          "underline",
          "align",
          "link",
          "image",
          "color",
          "background",
          "blockquote",
        ]}
        className="editor bg-gray-50 rounded-lg"
      />
      {showAiFab && !readOnly && typeof onAiFabClick === "function" && (
        <button
          type="button"
          onClick={onAiFabClick}
          disabled={aiFabDisabled}
          title="Restructure with AI"
          className="absolute bottom-3 right-3 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-purple-200 bg-white text-purple-600 shadow-md transition hover:bg-purple-50 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 dark:border-purple-800 dark:bg-gray-800 dark:text-purple-400 dark:hover:bg-gray-700"
        >
          <HiSparkles className="h-6 w-6" aria-hidden />
          <span className="sr-only">Restructure with AI</span>
        </button>
      )}
      <style jsx>{`
        /* Apply Playfair to headers */
        .ql-editor h1 {
          font-family: "Playfair Display", serif;
          font-size: 22px;
        }
        .ql-editor h2 {
          font-family: "Playfair Display", serif;
          font-size: 16px;
        }

        /* Apply Merriweather to the body text */
        .ql-editor p {
          font-family: "Merriweather Sans", sans-serif !important;
          font-size: 15px !important; /* Adjust size as needed */
          line-height: 1.6 !important; /* Custom line height */
          margin-top: 1rem !important; /* Space above paragraphs */
          margin-bottom: 1rem !important; /* Space below paragraphs */
        }

        /* Styling for the editor */
        .ql-editor {
          font-family: "Merriweather Sans", sans-serif;
          font-size: 15px; /* Default body font size */
          min-height: 100px; /* Minimum height for the editor */
          height: calc(100vh - 650px);
          padding-bottom: 3.5rem !important; /* space for AI FAB */
        }

        .ql-editor h1,
        .ql-editor h2 {
          margin-top: 1rem;
          margin-bottom: 1rem;
        }

        /* Placeholders for content sections */
        .ql-editor p strong {
          color: black;
        }

        .ql-editor p {
          color: #333;
        }

        .ql-editor h1 {
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default PostEditor;
